import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { Translations, SupportedLanguage } from './types'
import { de } from './de'
import { en } from './en'
import { sl } from './sl'
import { it } from './it'
import { es } from './es'
import { setEinheitenKuerzel } from '../domain/einheiten'

const TRANSLATIONS: Record<SupportedLanguage, Translations> = { de, en, sl, it, es }

const STORAGE_KEY = 'tschepp-lang'
const SUPPORTED: SupportedLanguage[] = ['de', 'en', 'sl', 'it', 'es']

function detectLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && SUPPORTED.includes(stored as SupportedLanguage)) return stored as SupportedLanguage
  const browser = navigator.language.split('-')[0]
  if (SUPPORTED.includes(browser as SupportedLanguage)) return browser as SupportedLanguage
  return 'de'
}

interface I18nContextValue {
  lang: SupportedLanguage
  setLang: (lang: SupportedLanguage) => void
  t: Translations
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<SupportedLanguage>(detectLanguage)

  // Gehzeit-Kuerzel liegen ausserhalb von React (LocationService, routes.ts
  // und die Karten-Rueckrufe formatieren ohne Kontext). Sie werden deshalb
  // wie eine Locale gesetzt — beim ersten Aufbau und bei jedem Wechsel.
  if (TRANSLATIONS[lang].units) setEinheitenKuerzel(TRANSLATIONS[lang].units)

  /* `useCallback` ist hier KEINE Optimierung, sondern Teil der Korrektheit:
   * Ohne sie bekommt `setLang` bei jedem Sprachwechsel eine neue Identitaet.
   * Fremde Effekte, die es in ihren Abhaengigkeiten fuehren, laufen dadurch
   * genau dann erneut, wenn die Sprache sich aendert — und einer davon
   * (NavigateEntryPage) spielte die gespeicherte Onboarding-Auswahl neu ein
   * und setzte die Sprache damit sofort wieder zurueck. Der Besucher tippte
   * „EN" und blieb auf Deutsch (Alex 2026-08-12, im Feld gemeldet). */
  const setLang = useCallback((next: SupportedLanguage) => {
    localStorage.setItem(STORAGE_KEY, next)
    setLangState(next)
  }, [])

  const wert = useMemo(() => ({ lang, setLang, t: TRANSLATIONS[lang] }), [lang, setLang])

  return (
    <I18nContext value={wert}>
      {children}
    </I18nContext>
  )
}

export function useT(): Translations {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useT must be used within I18nProvider')
  return ctx.t
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

export { SUPPORTED as SUPPORTED_LANGUAGES }
export type { SupportedLanguage, Translations }
